import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users,
  MousePointerClick,
  Calendar,
  ChevronDown,
  Trophy,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIMEFRAME_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'this-year', label: 'This Year' },
];

const CHART_COLORS = {
  t1: '#22c55e',      // green
  wasted: '#ef4444',  // red (was t2/amber)
  total: '#3b82f6',   // blue
  paid: '#22c55e',    // green
  pending: '#f59e0b', // amber
};

const PIE_COLORS = ['#22c55e', '#ef4444', '#6366f1', '#ec4899'];

export default function Analytics() {
  const [timeframe, setTimeframe] = useState('this-week');
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [models, setModels] = useState([]);
  
  const [overview, setOverview] = useState(null);
  const [vaPerformance, setVaPerformance] = useState(null);
  const [clicksTimeline, setClicksTimeline] = useState([]);
  const [activeVasTimeline, setActiveVasTimeline] = useState([]);
  const [topCountries, setTopCountries] = useState(null);

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        setModels(res.data.cupidbot_tracked_models || []);
      } catch (error) {
        console.error('Error fetching models:', error);
      }
    };
    fetchModels();
  }, []);

  const fetchAnalytics = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    const modelParam = selectedModel ? `&model_id=${selectedModel}` : '';
    
    try {
      const [overviewRes, performanceRes, clicksRes, activeVasRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/overview?timeframe=${timeframe}${modelParam}`),
        axios.get(`${API_URL}/analytics/va-performance?timeframe=${timeframe}${modelParam}`),
        axios.get(`${API_URL}/analytics/clicks-timeline?timeframe=${timeframe}${modelParam}`),
        axios.get(`${API_URL}/analytics/active-vas-timeline?timeframe=${timeframe}`),
        axios.get(`${API_URL}/analytics/top-countries?timeframe=${timeframe}${modelParam}`)
      ]);
      
      setOverview(overviewRes.data);
      setVaPerformance(performanceRes.data);
      setClicksTimeline(clicksRes.data.timeline || []);
      setActiveVasTimeline(activeVasRes.data.timeline || []);
      setTopCountries(countriesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeframe, selectedModel]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRefresh = () => {
    fetchAnalytics(true);
  };

  const getTimeframeLabel = () => {
    return TIMEFRAME_OPTIONS.find(opt => opt.value === timeframe)?.label || timeframe;
  };

  const getModelLabel = () => {
    if (!selectedModel) return 'All Models';
    const model = models.find(m => m.id === selectedModel);
    return model?.name || selectedModel;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' && entry.name.includes('$') 
                ? `$${entry.value.toFixed(2)}` 
                : entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Performance insights and metrics</p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          {/* Model Selector */}
          {models.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-zinc-700 text-white" data-testid="model-selector">
                  <Users className="w-4 h-4 mr-2" />
                  {getModelLabel()}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
                <DropdownMenuItem
                  onClick={() => setSelectedModel(null)}
                  className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                    !selectedModel ? 'bg-zinc-800 text-white' : ''
                  }`}
                >
                  All Models
                </DropdownMenuItem>
                {models.map(model => (
                  <DropdownMenuItem
                    key={model.id}
                    onClick={() => setSelectedModel(model.id)}
                    className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                      selectedModel === model.id ? 'bg-zinc-800 text-white' : ''
                    }`}
                  >
                    {model.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Chart Type Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chartType === 'line' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="chart-type-line"
            >
              Line
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                chartType === 'bar' 
                  ? 'bg-zinc-700 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="chart-type-bar"
            >
              Bar
            </button>
          </div>
          
          {/* Timeframe Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="border-zinc-700 text-white" data-testid="timeframe-selector">
                <Calendar className="w-4 h-4 mr-2" />
                {getTimeframeLabel()}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
              {TIMEFRAME_OPTIONS.map(option => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setTimeframe(option.value)}
                  className={`text-zinc-300 hover:text-white hover:bg-zinc-800 cursor-pointer ${
                    timeframe === option.value ? 'bg-zinc-800 text-white' : ''
                  }`}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-zinc-700 text-zinc-400 hover:text-white"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Clicks */}
        <div className="bg-card p-4 rounded-lg border border-blue-500/20">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <MousePointerClick className="w-5 h-5 text-blue-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">Total Clicks</p>
            <p className="text-2xl font-bold text-white">{overview?.clicks?.total?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              T1: {overview?.clicks?.t1?.toLocaleString() || 0} ({overview?.clicks?.t1_percentage || 0}%)
            </p>
          </div>
        </div>

        {/* T1 Clicks */}
        <div className="bg-card p-4 rounded-lg border border-green-500/20">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">T1 Clicks</p>
            <p className="text-2xl font-bold text-green-500">{overview?.clicks?.t1?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              ${overview?.financials?.base_earnings?.toFixed(2) || '0.00'} earnings
            </p>
          </div>
        </div>

        {/* Wasted Clicks */}
        <div className="bg-card p-4 rounded-lg border border-red-500/20">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-red-500/10 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">Wasted Clicks</p>
            <p className="text-2xl font-bold text-red-500">{overview?.clicks?.t2?.toLocaleString() || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {overview?.clicks?.t2 && overview?.clicks?.total ? ((overview.clicks.t2 / overview.clicks.total) * 100).toFixed(1) : 0}% of total
            </p>
          </div>
        </div>

        {/* Active VAs */}
        <div className="bg-card p-4 rounded-lg border border-amber-500/20">
          <div className="flex items-center justify-between">
            <div className="p-2 bg-amber-500/10 rounded-lg">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-sm text-zinc-400">
              {selectedModel ? 'VAs on Model' : 'Active VAs'}
            </p>
            <p className="text-2xl font-bold text-amber-500">{overview?.counts?.total_vas || 0}</p>
            <p className="text-xs text-zinc-500 mt-1">
              {selectedModel 
                ? `${overview?.counts?.total_invoices || 0} total invoices`
                : `${overview?.counts?.total_invoices || 0} invoices`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clicks Over Time Chart */}
        <div className="bg-card p-6 rounded-lg border border-blue-500/20">
          <h3 className="text-lg font-semibold text-white mb-4">Clicks Over Time</h3>
          <div className="h-[300px]">
            {clicksTimeline.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={clicksTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="t1_clicks" 
                      name="T1 Clicks"
                      stroke={CHART_COLORS.t1} 
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS.t1 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="wasted_clicks" 
                      name="Wasted Clicks"
                      stroke={CHART_COLORS.wasted} 
                      strokeWidth={2}
                      dot={{ fill: CHART_COLORS.wasted }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={clicksTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="t1_clicks" name="T1 Clicks" fill={CHART_COLORS.t1} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="wasted_clicks" name="Wasted Clicks" fill={CHART_COLORS.wasted} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-500">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Active VAs Chart */}
        <div className="bg-card p-6 rounded-lg border border-amber-500/20">
          <h3 className="text-lg font-semibold text-white mb-4">Active VAs</h3>
          <div className="h-[300px]">
            {activeVasTimeline.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={activeVasTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="active_vas" 
                      name="Active VAs"
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      dot={{ fill: '#f59e0b' }}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={activeVasTimeline}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="label" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="active_vas" name="Active VAs" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-zinc-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* VA Performance Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performers */}
        <div className="bg-card p-6 rounded-lg border border-green-500/20">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-5 h-5 text-green-500" />
            <h3 className="text-lg font-semibold text-white">Top Performers</h3>
          </div>
          <div className="space-y-3">
            {vaPerformance?.top_performers?.length > 0 ? (
              vaPerformance.top_performers.map((va, index) => (
                <div 
                  key={va.va_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  data-testid={`top-performer-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                      index === 1 ? 'bg-zinc-400/20 text-zinc-400' :
                      'bg-amber-700/20 text-amber-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">{va.name}</p>
                      <p className="text-xs text-zinc-500">{va.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-green-500 font-semibold">{va.t1_clicks.toLocaleString()} T1</p>
                    <p className="text-xs text-zinc-500">${va.earnings.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-zinc-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>

        {/* Under Performers */}
        <div className="bg-card p-6 rounded-lg border border-amber-500/20">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-white">Need Attention</h3>
          </div>
          <div className="space-y-3">
            {vaPerformance?.bottom_performers?.length > 0 ? (
              vaPerformance.bottom_performers.map((va, index) => (
                <div 
                  key={va.va_id}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  data-testid={`bottom-performer-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold bg-amber-500/20 text-amber-500">
                      <TrendingDown className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{va.name}</p>
                      <p className="text-xs text-zinc-500">{va.role}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-500 font-semibold">{va.t1_clicks.toLocaleString()} T1</p>
                    <p className="text-xs text-zinc-500">${va.earnings.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-zinc-500 text-center py-4">All VAs performing well!</p>
            )}
          </div>
        </div>
      </div>

      {/* Country Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clicks Distribution Pie */}
        <div className="bg-card p-6 rounded-lg border border-border">
          <h3 className="text-lg font-semibold text-white mb-4">Clicks Distribution</h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'T1 Clicks', value: overview?.clicks?.t1 || 0 },
                    { name: 'Wasted Clicks', value: overview?.clicks?.t2 || 0 },
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  <Cell fill={CHART_COLORS.t1} />
                  <Cell fill={CHART_COLORS.wasted} />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-zinc-400">T1</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-zinc-400">Wasted</span>
            </div>
          </div>
        </div>

        {/* Top T1 Countries */}
        <div className="bg-card p-6 rounded-lg border border-green-500/20">
          <h3 className="text-lg font-semibold text-white mb-4">Top T1 Countries</h3>
          <div className="space-y-3">
            {topCountries?.top_t1_countries?.length > 0 ? (
              topCountries.top_t1_countries.map((country, index) => (
                <div 
                  key={country.code}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  data-testid={`top-t1-country-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <span className="text-white font-medium">{country.name}</span>
                  </div>
                  <span className="text-green-500 font-semibold">{country.clicks.toLocaleString()}</span>
                </div>
              ))
            ) : (
              <p className="text-zinc-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>

        {/* Top Wasted Countries */}
        <div className="bg-card p-6 rounded-lg border border-red-500/20">
          <h3 className="text-lg font-semibold text-white mb-4">Top Wasted Countries</h3>
          <div className="space-y-3">
            {topCountries?.top_wasted_countries?.length > 0 ? (
              topCountries.top_wasted_countries.map((country, index) => (
                <div 
                  key={country.code}
                  className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                  data-testid={`top-wasted-country-${index}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{country.flag}</span>
                    <span className="text-white font-medium">{country.name}</span>
                  </div>
                  <span className="text-red-500 font-semibold">{country.clicks.toLocaleString()}</span>
                </div>
              ))
            ) : (
              <p className="text-zinc-500 text-center py-4">No data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
